import {
  ArrowRight,
  Printer,
  Scissors,
  Sparkles,
  User,
  Users,
} from "lucide-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { EarlyAccessForm } from "../components/EarlyAccessForm";
import { LanguagePair } from "../components/LanguagePair";
import { Logo } from "../components/Logo";
import { LanguageSwitch } from "../components/ReadingShell";
import { RotatingWord } from "../components/RotatingWord";
import { SheetPreview } from "../components/SheetPreview";
import { Button } from "../components/ui/Button";
import { Stepper } from "../components/ui/Stepper";
import { articlePath, listIndexableArticles } from "../content";
import { getElapsedDay } from "../domain";
import {
  DEFAULT_LOCALE,
  LOCALE_TAGS,
  localePath,
  useLocale,
  useMessages,
} from "../i18n";
import {
  ARTICLES_PATH,
  FAQ_PATH,
  MAX_SENTENCES,
  MAX_WORDS,
  MIN_SENTENCES,
  MIN_WORDS,
  createSettings,
  defaultSentenceCount,
  type LanguageCode,
} from "../shared";
import { useAppStore } from "../store/appStore";

/**
 * The order the headline cycles its languages in, opening on the one the picker
 * below already starts on. The names themselves come from the catalogue, so a
 * Thai reader is offered ไทย and a Dutch one Thai.
 */
const HERO_LANGUAGES: LanguageCode[] = ["th", "en", "nl"];

export function HomePage() {
  const locale = useLocale();
  const t = useMessages();
  const settings = useAppStore((state) => state.settings);
  const access = useAppStore((state) => state.access);
  const startJourney = useAppStore((state) => state.startJourney);
  const updateSettings = useAppStore((state) => state.updateSettings);
  const joinEarlyList = useAppStore((state) => state.joinEarlyList);
  const unlockFullCourse = useAppStore((state) => state.unlockFullCourse);
  const navigate = useNavigate();

  const [draft, setDraft] = useState(() =>
    settings ? { ...settings } : createSettings("th", "nl"),
  );
  /** Two names on the sheet is what turns it into a game for two. */
  const [players, setPlayers] = useState<"solo" | "duo">(() =>
    settings && (settings.leftLabel || settings.rightLabel) ? "duo" : "solo",
  );
  /** Set the moment the list accepts the address, so nobody is asked twice. */
  const [joined, setJoined] = useState(access.email !== null);
  const started = Boolean(settings);
  /** Where the learner stands today, so the list knows how far they got. */
  const currentDay = settings
    ? getElapsedDay(settings.startDate, settings.timezone)
    : 1;

  const heroLanguages = HERO_LANGUAGES.map((code) => t.languages[code]);
  const foreign = locale !== DEFAULT_LOCALE;
  const to = (path: string) => localePath(locale, path);

  const setPair = (pair: { left: LanguageCode; right: LanguageCode }) =>
    setDraft((current) => ({ ...current, ...pair }));

  const setWords = (words: number) =>
    setDraft((current) => ({
      ...current,
      words,
      sentences:
        current.sentences === defaultSentenceCount(current.words)
          ? defaultSentenceCount(words)
          : current.sentences,
    }));

  const go = () => {
    const names =
      players === "duo"
        ? {
            leftLabel: draft.leftLabel.trim(),
            rightLabel: draft.rightLabel.trim(),
          }
        : { leftLabel: "", rightLabel: "" };

    if (started) {
      updateSettings({
        left: draft.left,
        right: draft.right,
        words: draft.words,
        sentences: draft.sentences,
        ...names,
      });
    } else {
      startJourney(
        createSettings(draft.left, draft.right, {
          words: draft.words,
          sentences: draft.sentences,
          ...names,
        }),
      );
    }
    navigate("/today");
  };

  /** The course opens on the confirmation, not on the submit. */
  const openPremium = () => {
    unlockFullCourse();
    go();
  };

  return (
    <div className="home">
      <header className="home__header">
        <Link to={to("/")} aria-label="SprinkleDays home">
          <Logo />
        </Link>
        <div className="home__header-end">
          <LanguageSwitch path="/" />
          {started ? (
            <Link className="btn btn--ghost btn--md" to="/today">
              <span>{t.nav.openToday}</span>
            </Link>
          ) : (
            <a className="home__header-link" href="#how">
              {t.nav.howItWorks}
            </a>
          )}
        </div>
      </header>

      <main className="hero">
        <div className="hero__copy">
          {/*
            The headline never names a word count. The pace is a setting, and a
            number here reads as the product's promise no matter which number it
            is, so the stepper below is the only place a count appears.
          */}
          <h1 className="hero__title">
            {t.home.headline.lead}
            <RotatingWord words={heroLanguages} />
            <span className="visually-hidden">
              {t.languages.or(heroLanguages)}
            </span>
            <br />
            <em>{t.home.headline.emphasis}</em>
            {t.home.headline.tail}
          </h1>
          <p className="hero__lede">{t.home.lede}</p>

          <div className="start">
            <LanguagePair
              left={draft.left}
              right={draft.right}
              leftLabel={t.home.learning}
              rightLabel={t.home.speak}
              onChange={setPair}
            />

            <div className="start__counts">
              <Stepper
                label={t.home.wordsADay}
                value={draft.words}
                min={MIN_WORDS}
                max={MAX_WORDS}
                unit={t.app.unitWord}
                onChange={setWords}
              />
              <Stepper
                label={t.home.sentences}
                value={draft.sentences}
                min={MIN_SENTENCES}
                max={MAX_SENTENCES}
                unit={t.app.unitSentence}
                onChange={(sentences) =>
                  setDraft((current) => ({ ...current, sentences }))
                }
              />
            </div>

            <div className="start__who">
              <span className="start__who-label" id="players-label">
                {t.home.whoIsLearning}
              </span>
              <div
                className="segmented"
                role="group"
                aria-labelledby="players-label"
              >
                <button
                  type="button"
                  className="segmented__btn"
                  aria-pressed={players === "solo"}
                  onClick={() => setPlayers("solo")}
                >
                  <User size={17} aria-hidden="true" />
                  {t.home.justMe}
                </button>
                <button
                  type="button"
                  className="segmented__btn"
                  aria-pressed={players === "duo"}
                  onClick={() => setPlayers("duo")}
                >
                  <Users size={17} aria-hidden="true" />
                  {t.home.twoOfUs}
                </button>
              </div>
            </div>

            {players === "duo" ? (
              <div className="start__names">
                <label className="field">
                  <span>{t.home.nameLeft}</span>
                  <input
                    value={draft.leftLabel}
                    maxLength={24}
                    placeholder={t.home.namePlaceholderLeft}
                    onChange={(event) =>
                      setDraft((current) => ({
                        ...current,
                        leftLabel: event.target.value,
                      }))
                    }
                  />
                </label>
                <label className="field">
                  <span>{t.home.nameRight}</span>
                  <input
                    value={draft.rightLabel}
                    maxLength={24}
                    placeholder={t.home.namePlaceholderRight}
                    onChange={(event) =>
                      setDraft((current) => ({
                        ...current,
                        rightLabel: event.target.value,
                      }))
                    }
                  />
                </label>
                <p className="start__hint">{t.home.namesHint}</p>
              </div>
            ) : null}

            <Button size="lg" icon={<ArrowRight size={19} />} onClick={go}>
              {started ? t.home.save : t.home.start}
            </Button>

            <p className="start__fine">{t.home.fine}</p>
          </div>
        </div>
      </main>

      <section className="sample">
        <div className="sample__copy">
          <p className="tag tag--sun">{t.home.sampleTag}</p>
          <h2>{t.home.sampleTitle}</h2>
          <p className="sample__lede">{t.home.sampleLede}</p>

          <ul className="sample__notes">
            {t.home.sampleNotes.map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        </div>

        <div className="sample__paper">
          <SheetPreview
            left={draft.left}
            right={draft.right}
            words={draft.words}
            sentences={draft.sentences}
          />
        </div>
      </section>

      <section className="how" id="how">
        <h2 className="how__title">{t.home.howTitle}</h2>
        <ol className="how__steps">
          {t.home.steps.map((step, index) => {
            const Icon = [Sparkles, Printer, Scissors][index] ?? Sparkles;

            return (
              <li className="step" key={step.title}>
                <span className="step__icon" aria-hidden="true">
                  <Icon size={20} />
                </span>
                <h3>{step.title}</h3>
                <p>{step.body}</p>
              </li>
            );
          })}
        </ol>
      </section>

      <section className="premium" id="premium">
        <div className="premium__card">
          <p className="tag tag--sun">{t.home.premiumTag}</p>

          {joined ? (
            <>
              <h2>
                <strong>{t.home.premiumJoinedTitle}</strong>
              </h2>
              <p className="premium__lede">{t.home.premiumJoinedLede}</p>
              <Button
                size="lg"
                icon={<ArrowRight size={19} />}
                onClick={openPremium}
              >
                {started ? t.nav.openToday : t.home.start}
              </Button>
            </>
          ) : (
            <>
              <h2>
                <strong>{t.home.premiumTitleStrong}</strong>
                {t.home.premiumTitleRest}
              </h2>
              <p className="premium__lede">{t.home.premiumLede}</p>

              <p className="premium__ask">{t.home.premiumAsk}</p>

              <EarlyAccessForm
                settings={draft}
                day={currentDay}
                submitLabel={t.home.premiumSubmit}
                onJoined={(email) => {
                  joinEarlyList(email);
                  setJoined(true);
                }}
              />

              <p className="premium__fine">{t.home.premiumFine}</p>
            </>
          )}
        </div>
      </section>

      <section className="reads">
        <div className="reads__copy">
          <p className="tag tag--sun">{t.home.readsTag}</p>
          <h2>{t.home.readsTitle}</h2>
          <p>{t.home.readsBody}</p>
          {foreign ? <p className="reads__note">{t.home.readsInEnglish}</p> : null}
        </div>
        <ul className="reads__list">
          {listIndexableArticles()
            .slice(0, 6)
            .map((article) => (
              <li key={article.slug}>
                <Link
                  to={articlePath(article.slug)}
                  hrefLang={foreign ? LOCALE_TAGS[DEFAULT_LOCALE] : undefined}
                  lang={foreign ? LOCALE_TAGS[DEFAULT_LOCALE] : undefined}
                >
                  <span aria-hidden="true">{article.emoji}</span>
                  <span>
                    <strong>{article.title}</strong>
                    <small>{article.description}</small>
                  </span>
                </Link>
              </li>
            ))}
        </ul>
        <p className="reads__more">
          <Link to={to(ARTICLES_PATH)}>{t.nav.allGuides}</Link> ·{" "}
          <Link to={to(FAQ_PATH)}>{t.nav.questions}</Link>
        </p>
      </section>

      <footer className="home__footer">
        <Logo />
        <p>{t.home.footerBlurb}</p>
        <nav className="home__footer-nav" aria-label={t.nav.footer}>
          <Link to={to(ARTICLES_PATH)}>{t.nav.guides}</Link>
          <Link to={to(FAQ_PATH)}>{t.nav.questions}</Link>
        </nav>
      </footer>
    </div>
  );
}
